import React, { useState, useRef, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUI } from '../UIContext';
import Login from '../Login';
import PageWrapper from '../components/PageWrapper';
import { FaMusic } from 'react-icons/fa';
import { useActiveSession } from '../ActiveSessionContext';
import myIcon from '../assets/logo512.png'; // adjust path
import { normalizeKey, normalizeType, keyRoot, normalizeGenre } from '../utils/normalize';
import { useUserPrefs } from '../UserPrefsProvider'; // near the top
import AbcPreview from '../components/AbcPreview';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import TagFilterControl from '../components/TagFilterControl'; // adjust if needed
import CustomTagInput from '../components/CustomTagInput';
import { CgEnter } from 'react-icons/cg';
import { loadTunesSmart } from '../utils/offlineLoaders';
import { loadTunesHybrid } from '../utils/offlineLoaders';
import dexie from '../offlineDB';
import SessionPromptModal from '../components/SessionPromptModal';

export default function SearchPage() {
  const { user } = useAuth();

  const { inputStyle, textStyle } = useUI();

  const { activeSession } = useActiveSession();
  const [tunes, setTunes] = useState([]);
  const [selectedTuneIds, setSelectedTuneIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const navigate = useNavigate();
const [searchQuery, setSearchQuery] = useState(null);
const [filterKey, setFilterKey] = useState(null);
const [filterType, setFilterType] = useState(null);
const [filterGenre, setFilterGenre] = useState(null);
const [filterStatus, setFilterStatus] = useState('—');
const [filterRating, setFilterRating] = useState('—');
const [filterRatingLabel, setFilterRatingLabel] = useState('—');
const [sessionConfirmations, setSessionConfirmations] = useState({});
const [selectedTags, setSelectedTags] = useState([]);
const [tagLogic, setTagLogic] = useState('AND'); // or 'OR'
const [showTagFilters, setShowTagFilters] = useState(false);
const [showBulkTagInput, setShowBulkTagInput] = useState(false);
const [bulkTags, setBulkTags] = useState([]);
const [dexieReady, setDexieReady] = useState(false);
const location = useLocation();
const [filtered, setFiltered] = useState([]);
const [pendingSessionTune, setPendingSessionTune] = useState(null);


useEffect(() => {
  dexie.open().then(() => setDexieReady(true));
}, []);

  const [sortOrder, setSortOrder] = useState('newest');
  const [bulkStatus, setBulkStatus] = useState('Learned');
const maybePrefsHook = useUserPrefs();
const prefs = user ? maybePrefsHook?.prefs : null;
const [bulkActionValue, setBulkActionValue] = useState('');

  const boxRef = useRef(null);
const [scrollHeight, setScrollHeight] = useState(300); // safe default
const [collapsed, setCollapsed] = useState(false);

const updateHeight = () => {
  if (boxRef.current) {
    const boxHeight = boxRef.current.offsetHeight;
    const available = window.innerHeight - boxHeight - 40; // 40 for bottom margin/padding
    setScrollHeight(Math.max(100, available));
  }
};

useEffect(() => {
  updateHeight();
  const handleResize = () => updateHeight();
  window.addEventListener('resize', handleResize);

  const resizeObserver = new ResizeObserver(() => updateHeight());
  if (boxRef.current) resizeObserver.observe(boxRef.current);

  return () => {
    window.removeEventListener('resize', handleResize);
    resizeObserver.disconnect();
  };
}, [collapsed]); // recalculate when collapsed changes


 useEffect(() => {
  if (!user) return;

  const loadFilters = async () => {
    try {
      const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
      const snap = await getDoc(ref);
      const savedFilters = snap.exists() ? (snap.data().searchFilters || {}) : {};

      const urlParams = new URLSearchParams(window.location.search);
      const tagParam = urlParams.get('tag');

      if (tagParam) {
        // If coming from a tag click, show tag UI and set tag
        setSearchQuery('');
        setFilterKey('');
        setFilterType('');
        setFilterGenre('');
        setFilterStatus('');
        setFilterRating('');
        setFilterRatingLabel('');
        setSelectedTags([tagParam]);
        setTagLogic('AND');
        setShowTagFilters(true); // ✅ Expand tag section
      } else {
        // Load filters normally
        setSearchQuery(savedFilters.searchQuery ?? '');
        setFilterKey(savedFilters.filterKey ?? '');
        setFilterType(savedFilters.filterType ?? '');
        setFilterGenre(savedFilters.filterGenre ?? '');
        setFilterStatus(savedFilters.filterStatus ?? '—');
        setFilterRating(savedFilters.filterRating ?? '—');
        setFilterRatingLabel(savedFilters.filterRatingLabel ?? '—');
        setSelectedTags([]);
        setTagLogic('AND');
        setShowTagFilters(false); // ✅ Collapse unless toggled
      }
    } catch (err) {
      console.error('Failed to load filters from Firebase:', err);
    }
  };

  loadFilters();
}, [user]);


useEffect(() => {
  if (
    searchQuery == null ||
    filterKey == null ||
    filterType == null ||
    filterGenre == null ||
    filterStatus == null || filterStatus === '—' || filterStatus === '' ||
    filterRating == null || filterRating === '—' || filterRating === '' ||
    filterRatingLabel == null || filterRatingLabel === '—' || filterRatingLabel === '' ||
    !user
  ) return;


  const saveFilters = async () => {
    try {
      const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
      await setDoc(ref, {
        searchFilters: {
          searchQuery,
          filterKey,
          filterType,
          filterGenre,
          filterStatus,
          filterRating,
          filterRatingLabel
        }
      }, { merge: true });
    } catch (err) {
      console.error('Failed to save filters to Firebase:', err);
    }
  };

  saveFilters();
}, [
  user,
  searchQuery,
  filterKey,
  filterType,
  filterGenre,
  filterStatus,
  filterRating,
  filterRatingLabel
]);



const clearAllFilters = () => {
  setSearchQuery('');
  setFilterKey('');
  setFilterType('');
  setFilterGenre('');
  setFilterStatus('');
  setFilterRating('');
  setFilterRatingLabel('');
  setSelectedTags([]);        // ✅ clear tag pills
  setTagLogic('AND');         // ✅ optionally reset logic too
};







  const getFilteredSubset = (ignoreField) => {
  return tunes.filter(tune => {
    const matchesSearch = Object.values(tune).some(val =>
      typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const nk = normalizeKey(tune.key);
    const matchMap = {
      genre: !filterGenre || (tune.genre && tune.genre.toLowerCase() === filterGenre.toLowerCase()),
      type: !filterType || (tune.type && normalizeType(tune.type) === normalizeType(filterType)),
      key: !filterKey || nk === filterKey || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', '')),
      status: !filterStatus || (filterStatus === '—' ? !tune.status : tune.status === filterStatus),
      rating: !filterRating || (filterRating === '—' ? !tune.rating : tune.rating === filterRating),
      ratingLabel: !filterRatingLabel || (filterRatingLabel === '—' ? !tune.ratingLabel : tune.ratingLabel === filterRatingLabel),
    };

    delete matchMap[ignoreField];

    const restMatch = Object.values(matchMap).every(Boolean);

    return matchesSearch && restMatch;
  });
};


  const keyGroups = {};
  tunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });





  const filteredTunes = tunes.filter(tune => {
    const tagMatch = (tune) => {
      if (selectedTags.length === 0) return true;
      const tags = tune.customTags || [];
      return tagLogic === 'AND'
        ? selectedTags.every(tag => tags.includes(tag))
        : selectedTags.some(tag => tags.includes(tag));
    };

    const matchesSearch = Object.values(tune).some(val =>
      typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const nk = normalizeKey(tune.key);
    const matchesKey = !filterKey
      || nk === filterKey
      || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', ''));
    const matchesType = !filterType || (tune.type && normalizeType(tune.type) === normalizeType(filterType));
    const matchesGenre = !filterGenre || normalizeGenre(tune.genre) === filterGenre;
    const matchesStatus =
      !filterStatus ||
      (filterStatus === '—' ? !tune.status : tune.status === filterStatus);

    const matchesRating =
      !filterRating ||
      (filterRating === '—' ? !tune.rating : tune.rating === filterRating);

    const matchesTags = tagMatch(tune);

  
return (
  matchesSearch &&
  matchesKey &&
  matchesType &&
  matchesGenre &&
  matchesStatus &&
  matchesRating &&
  matchesTags
);

  });

  const sortedTunes = React.useMemo(() => {
    const tunesCopy = [...filteredTunes];

    if (sortOrder === 'alpha') {
      tunesCopy.sort((a, b) => a.title.localeCompare(b.title));
    } else {
 tunesCopy.forEach(tune => {
  const ts = tune.createdAt;

  if (ts?.toMillis) {
    tune._createdAtTime = ts.toMillis();
  } else if (ts?.toDate) {
    tune._createdAtTime = ts.toDate().getTime();
  } else if (typeof ts === 'object' && typeof ts.seconds === 'number') {
    // 🔧 Add this fallback
    tune._createdAtTime = ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6);
  } else if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    tune._createdAtTime = isNaN(parsed) ? 0 : parsed;
  } else if (typeof ts === 'number') {
    tune._createdAtTime = ts;
  } else {
    tune._createdAtTime = 0;
  }
});


      tunesCopy.sort((a, b) => {
        if (a._createdAtTime === b._createdAtTime) {
          return a.id.localeCompare(b.id);
        }
        return sortOrder === 'oldest'
          ? a._createdAtTime - b._createdAtTime
          : b._createdAtTime - a._createdAtTime;
      });

      tunesCopy.forEach(tune => delete tune._createdAtTime);
    }

    return tunesCopy;
  }, [filteredTunes, sortOrder]);

  const toggleTuneSelection = (id) => {
    setSelectedTuneIds(prev =>
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTuneIds.length === filteredTunes.length) {
      setSelectedTuneIds([]);
    } else {
      setSelectedTuneIds(sortedTunes.map(t => t.id));
    }
  };

  const deleteSelectedTunes = async () => {
  if (!user || selectedTuneIds.length === 0) return;
  const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedTuneIds.length} tune(s)?`);
  if (!confirmDelete) return;

  const tunesToDelete = [];

  for (const id of selectedTuneIds) {
    let data;
    if (navigator.onLine) {
      const ref = doc(db, 'users', user.uid, 'tunes', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        data = snap.data();
      }
    } else {
      data = await dexie.tunes.get(id);
    }

    if (data) {
      tunesToDelete.push({ id, data });
    }
  }

  let affectedSets = [];
  if (navigator.onLine) {
    const setsSnapshot = await getDocs(collection(db, 'users', user.uid, 'sets'));
    setsSnapshot.forEach(docSnap => {
      const set = docSnap.data();
      const matchingTunes = (set.tunes || []).filter(t => selectedTuneIds.includes(t.id));
      if (matchingTunes.length > 0) {
        affectedSets.push({
          id: docSnap.id,
          title: set.title,
          originalTunes: set.tunes,
          removedTunes: matchingTunes,
        });
      }
    });
  } else {
    const allSets = await dexie.sets.toArray();
    affectedSets = allSets
      .filter(set => (set.tunes || []).some(t => selectedTuneIds.includes(t.id)))
      .map(set => ({
        id: set.id,
        title: set.title,
        originalTunes: set.tunes,
        removedTunes: set.tunes.filter(t => selectedTuneIds.includes(t.id)),
      }));
  }

  setPendingDelete({ tunes: tunesToDelete, sets: affectedSets });
  setSelectedTuneIds([]);

  const timer = setTimeout(async () => {
    for (const { id } of tunesToDelete) {
      if (navigator.onLine) {
        await deleteDoc(doc(db, 'users', user.uid, 'tunes', id));
      } else {
        await dexie.pendingWrites.put({
          id,
          type: 'tune',
          operation: 'delete',
        });
      }
      await dexie.tunes.delete(id);
    }

    for (const set of affectedSets) {
      const updatedTunes = (set.originalTunes || []).filter(t => !selectedTuneIds.includes(t.id));

      if (navigator.onLine) {
        await updateDoc(doc(db, 'users', user.uid, 'sets', set.id), {
          tunes: updatedTunes
        });
      } else {
        const setData = await dexie.sets.get(set.id);
        if (setData) {
          await dexie.sets.put({
            ...setData,
            tunes: updatedTunes
          });
          await dexie.pendingWrites.put({
            id: set.id,
            type: 'set',
            operation: 'update',
            data: { ...setData, tunes: updatedTunes }
          });
        }
      }
    }

    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;
  clearTimeout(undoTimer);

  for (const { id, data } of pendingDelete.tunes) {
    if (navigator.onLine) {
      await setDoc(doc(db, 'users', user.uid, 'tunes', id), data);
    }
    await dexie.tunes.put({ id, ...data });
  }

  for (const set of pendingDelete.sets) {
    const mergedTunes = [...(set.originalTunes || []), ...set.removedTunes];
    const dedupedTunes = Array.from(
      new Map(mergedTunes.map(t => [t.id, t])).values()
    );

    if (navigator.onLine) {
      const setRef = doc(db, 'users', user.uid, 'sets', set.id);
      const snap = await getDoc(setRef);
      if (snap.exists()) {
        await updateDoc(setRef, { tunes: dedupedTunes });
      }
    }

    const localSet = await dexie.sets.get(set.id);
    if (localSet) {
      await dexie.sets.put({
        ...localSet,
        tunes: dedupedTunes
      });

      if (!navigator.onLine) {
        await dexie.pendingWrites.put({
          id: set.id,
          type: 'set',
          operation: 'update',
          data: {
            ...localSet,
            tunes: dedupedTunes
          }
        });
      }
    }
  }

  setPendingDelete(null);
  setUndoTimer(null);
};

const updateStatusForSelectedTunes = async (newStatus) => {
  if (!user || selectedTuneIds.length === 0) return;

  const confirmUpdate = window.confirm(
    `Set status to "${newStatus}" for ${selectedTuneIds.length} tune(s)?`
  );
  if (!confirmUpdate) return;

  try {
    for (const id of selectedTuneIds) {
      const ref = doc(db, 'users', user.uid, 'tunes', id);
      const updated = { status: newStatus };

      if (navigator.onLine) {
        await updateDoc(ref, updated);
        await dexie.tunes.update(id, updated);
      } else {
        await dexie.tunes.update(id, updated);
        await dexie.pendingWrites.put({
          id,
          type: 'tune',
          operation: 'update',
          data: { id, ...updated }
        });
      }
    }
    setSelectedTuneIds([]);
  } catch (err) {
    console.error('Error updating statuses:', err);
    alert('Failed to update statuses.');
  }
};


const typeOptions = [...new Set(tunes.map(t => normalizeType(t.type)).filter(Boolean))].sort();
const genreOptions = [...new Set(tunes.map(t => normalizeGenre(t.genre)).filter(Boolean))].sort();
const ratingOptions = [...new Set(tunes.map(t => t.rating).filter(Boolean))].sort();           // ★, ★★, etc.



const genreCounts = {};
getFilteredSubset("genre").forEach(t => {
  const g = normalizeGenre(t.genre);
  if (g) genreCounts[g] = (genreCounts[g] || 0) + 1;
});


const typeCounts = {};
getFilteredSubset("type").forEach(t => {
  const type = normalizeType(t.type);
  if (type) typeCounts[type] = (typeCounts[type] || 0) + 1;
});

const keyCounts = {};
getFilteredSubset("key").forEach(t => {
  const nk = normalizeKey(t.key);
  if (nk) keyCounts[nk] = (keyCounts[nk] || 0) + 1;
});

const statusCounts = {};
getFilteredSubset("status").forEach(t => {
  const key = t.status || '—';
  statusCounts[key] = (statusCounts[key] || 0) + 1;
});

const ratingCounts = {};
getFilteredSubset("rating").forEach(t => {
  const raw = t.rating;
  const key = (!raw || raw === '—') ? '' : raw;  // normalize all to '' if blank or '—'
  ratingCounts[key] = (ratingCounts[key] || 0) + 1;
});


const labelCounts = {};
getFilteredSubset("ratingLabel").forEach(t => {
  const key = t.ratingLabel || '—';
  labelCounts[key] = (labelCounts[key] || 0) + 1;
});


const saveCurrentFilters = async () => {
  if (!user) return;

  const filtersToSave = {
    searchQuery,
    filterKey,
    filterType,
    filterGenre,
    filterStatus,
    filterRating,
    filterRatingLabel,
    selectedTags
  };

  try {
    localStorage.setItem('activeSearchFilters', JSON.stringify(filtersToSave));
    const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
    await setDoc(ref, { searchFilters: filtersToSave }, { merge: true });
  } catch (err) {
    console.error('Failed to save filters to Firebase:', err);
  }
};

const handleBulkTagConfirm = async () => {
  if (!user || selectedTuneIds.length === 0 || bulkTags.length === 0) return;

  const confirmed = window.confirm(`Add tag(s) to ${selectedTuneIds.length} tune(s)?`);
  if (!confirmed) return;

  try {
    for (const id of selectedTuneIds) {
      const ref = doc(db, 'users', user.uid, 'tunes', id);

      let currentData;
      if (navigator.onLine) {
        const snap = await getDoc(ref);
        if (!snap.exists()) continue;
        currentData = snap.data();
      } else {
        currentData = await dexie.tunes.get(id);
        if (!currentData) continue;
      }

      const existing = currentData.customTags || [];
      const merged = Array.from(new Set([...existing, ...bulkTags]));
      const updated = { ...currentData, customTags: merged };

      if (navigator.onLine) {
        await updateDoc(ref, { customTags: merged });
        await dexie.tunes.put({ id, ...updated });
      } else {
        await dexie.tunes.put({ id, ...updated });
        await dexie.pendingWrites.put({
          id,
          type: 'tune',
          operation: 'update',
          data: { id, ...updated },
        });
      }
    }

    setShowBulkTagInput(false);
    setBulkTags([]);
    setSelectedTuneIds([]);
  } catch (err) {
    console.error('Failed to update tags:', err);
    alert('Failed to apply tags.');
  }
};



const filtersReady = [
  searchQuery,
  filterKey,
  filterType,
  filterGenre,
  filterStatus,
  filterRating,
  filterRatingLabel
].every(val => val !== null);

useEffect(() => {
  if (!user || !filtersReady) return;
  saveCurrentFilters();
}, [
  user,
  filtersReady,
  searchQuery,
  filterKey,
  filterType,
  filterGenre,
  filterStatus,
  filterRating,
  filterRatingLabel,
  selectedTags
]);



useEffect(() => {
  if (!user || !filtersReady) return;
  return () => {
    saveCurrentFilters(); // async is okay here; fire-and-forget
  };
}, [user, filtersReady]);


const goToRandomTune = async () => {
  if (sortedTunes.length === 0) return;
  await saveCurrentFilters();
  const randomTune = sortedTunes[Math.floor(Math.random() * sortedTunes.length)];
  navigate(`/tune/${randomTune.id}`);
};



useEffect(() => {
  if (!user) return;

  const handleBeforeUnload = () => {
    // Not awaited — that's fine
    saveCurrentFilters();
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    saveCurrentFilters(); // Also catch React Router unmounts
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [user, searchQuery, filterKey, filterType, filterGenre, filterStatus, filterRating, filterRatingLabel, selectedTags]);

useEffect(() => {
  if (!dexieReady || !user) return;

  let unsub = null;

  const loadTunes = async () => {
    if (navigator.onLine) {
      const ref = collection(db, 'users', user.uid, 'tunes');
      unsub = onSnapshot(ref, snapshot => {
        const liveTunes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setTunes(liveTunes);
        dexie.tunes.bulkPut(liveTunes);
      });
    } else {
      const offlineTunes = await dexie.tunes.toArray();
      setTunes(offlineTunes);
    }
  };

  loadTunes();

  return () => {
    if (unsub) unsub();
  };
}, [user, dexieReady]);


if (!user) return <Login />;

if (!dexieReady) {
  return (
    <PageWrapper>
      <p style={{ padding: '2rem' }}>Loading offline data…</p>
    </PageWrapper>
  );
}


  return (
    <PageWrapper>

      
      {!user ? (
        <Login />
      ) : (
        <>

  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>


<BorderedBox
  ref={boxRef}
  style={{ marginBottom: '0.7rem' }}
  collapsible
  onCollapseChange={updateHeight} // optional trigger
  exposeCollapsed={(val) => setCollapsed(val)}
>



      
  <div className="app-title">
    <img src={myIcon} alt="Logo" className="responsive-icon" />
  <span>AllMyTunes</span>
  </div>


        
<div style={{ maxWidth: '600px', margin: '0 auto' }}>

<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.7rem',
    maxWidth: '750px',
    margin: '0 auto 1rem auto',
    alignItems: 'center',
  }}
>


  <input
    className="search-input"
    placeholder="Search any field (title, genre, key, type, abc, lyrics, notes...)"
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    style={{ ...inputStyle, gridColumn: '1 / -1' }}
  />

  <select style={inputStyle} className={filterGenre ? "filter-active" : ""} value={filterGenre} onChange={(e) => setFilterGenre(e.target.value)}>
    <option value="">All Genres</option>
    {genreOptions.map((g, i) => (
      <option key={i} value={g}>{g} ({genreCounts[g] || 0})</option>
    ))}
  </select>

  <select style={inputStyle} className={filterType ? "filter-active" : ""} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
    <option value="">All Types</option>
    {typeOptions.map((t, i) => (
      <option key={i} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)} ({typeCounts[t] || 0})</option>
    ))}
  </select>

  <select style={inputStyle} className={filterKey ? "filter-active" : ""} value={filterKey} onChange={(e) => setFilterKey(e.target.value)}>
    <option value="">All Keys</option>
    {Object.keys(keyGroups).sort().map(root => (
      <optgroup key={root} label={root}>
        <option value={`root:${root}`}>{root} (All Modes)</option>
        {[...keyGroups[root]].sort((a, b) => {
          const rank = k => k.endsWith(' mix') ? 3 : k.endsWith(' dorian') ? 2 : k.endsWith('m') ? 1 : 0;
          return rank(a) - rank(b) || a.localeCompare(b);
        }).map(k => {
          const label =
            k.endsWith('m') ? root + ' minor' :
            k.endsWith(' dorian') ? root + ' dorian' :
            k.endsWith(' mix') ? root + ' mixolydian' :
            root + ' major';
          return (
            <option key={k} value={k}>{label} ({keyCounts[k] || 0})</option>
          );
        })}
      </optgroup>
    ))}
  </select>

  <select style={inputStyle} className={filterStatus ? "filter-active" : ""} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
      <option value="">All Statuses</option>
<option value="—">No Status ({statusCounts['—'] || 0})</option>
      {['Need to Learn', 'Learning', 'Learned'].map((s, i) => (
        <option key={i} value={s}>{s} ({statusCounts[s] || 0})</option>
      ))}
  </select>

  <select style={inputStyle} className={filterRating ? "filter-active" : ""} value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
      <option value="">All Ratings</option>
<option value="">No Rating ({ratingCounts[''] || 0})</option>
{ratingOptions
  .filter(r => r && r !== '—') // remove invalid placeholder
  .map((r, i) => (
    <option key={i} value={r}>{r} ({ratingCounts[r] || 0})</option>
))}

  </select>

<button
  onClick={() => setShowTagFilters(p => !p)}
  style={{
    ...inputStyle,
    backgroundColor: selectedTags.length > 0 ? '#228be6' : '#444',
    color: 'white'
  }}
>
  {showTagFilters
    ? `✖ Hide Tags${selectedTags.length > 0 ? ` (${filteredTunes.length})` : ''}`
    : `🏷️ Filter by Tags${selectedTags.length > 0 ? ` (${filteredTunes.length})` : ''}`}
</button>






{showTagFilters && (
  <TagFilterControl
    availableTags={prefs?.customTags || []}
    selectedTags={selectedTags}
    setSelectedTags={setSelectedTags}
    tagLogic={tagLogic}
    setTagLogic={setTagLogic}
  />
)}



 <div
  style={{
    gridColumn: '1 / -1',
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginTop: '0.2rem'
  }}
>

   <button
  onClick={clearAllFilters}
  style={{ ...inputStyle, backgroundColor: '#444', color: 'white' }}
>
  🚫 Clear Filters
</button>

<button
  onClick={goToRandomTune}
  style={{ ...inputStyle, backgroundColor: '#444', color: 'white' }}
>
  🎲 Random Tune!
</button>

  </div>
</div>

</div>
         

          <div className="tune-toolbar">
            <div className="tune-toolbar-row">
              <select
                style={inputStyle}
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="alpha">A–Z</option>
              </select>

              <button
                style={{ ...inputStyle, backgroundColor: '#444', color: 'white' }}
                onClick={toggleSelectAll}
              >
                {selectedTuneIds.length === filteredTunes.length ? 'Deselect All' : 'Select All'}
              </button>

            {/*
                          <button
                style={{ ...inputStyle, backgroundColor: 'darkred', color: 'white' }}
                onClick={deleteSelectedTunes}
                disabled={selectedTuneIds.length === 0}
              >
                Delete Selected ({selectedTuneIds.length})
              </button>  

            */}
        
             <select
            style={inputStyle}
            value={bulkActionValue}
            onChange={async (e) => {
              const value = e.target.value;
              if (!value || selectedTuneIds.length === 0) return;

              setBulkActionValue(''); // reset immediately

              if (value === 'action:delete') {
                deleteSelectedTunes();
                return;
              }

             if (value === 'action:addset') {
  if (!user) return;

  const confirm = window.confirm(`Create new set with ${selectedTuneIds.length} tune(s)?`);
  if (!confirm) return;

  const orderedTunes = selectedTuneIds
    .map(id => tunes.find(t => t.id === id))
    .filter(Boolean)
    .map(t => ({
      id: t.id,
      title: t.title || '',
      key: t.key || '',
      type: t.type || '',
    }));

  const ref = doc(collection(db, 'users', user.uid, 'sets'));
  const createdAt = new Date().toISOString();
  const newSet = {
    id: ref.id,
    title: orderedTunes[0]?.title || 'Untitled Set',
    createdAt,
    tunes: orderedTunes
  };

  if (navigator.onLine) {
    await setDoc(ref, newSet);
    await dexie.sets.put(newSet);
  } else {
    await dexie.sets.put(newSet);
    await dexie.pendingWrites.put({
      id: newSet.id,
      type: 'set',
      operation: 'add',
      data: newSet
    });
  }

  for (const t of orderedTunes) {
    const tuneRef = doc(db, 'users', user.uid, 'tunes', t.id);

    let currentTune;
    if (navigator.onLine) {
      const snap = await getDoc(tuneRef);
      if (!snap.exists()) continue;
      currentTune = snap.data();
    } else {
      currentTune = await dexie.tunes.get(t.id);
    }

    if (!currentTune) continue;

    const existingSets = currentTune.sets || [];
    const newSetEntry = {
      setId: newSet.id,
      title: newSet.title,
      createdAt: newSet.createdAt
    };

    const updatedSets = [...existingSets.filter(s => s.setId !== newSet.id), newSetEntry];
    const updatedTune = { ...currentTune, sets: updatedSets };

    if (navigator.onLine) {
      await updateDoc(tuneRef, { sets: updatedSets });
      await dexie.tunes.put({ id: t.id, ...updatedTune });
    } else {
      await dexie.tunes.put({ id: t.id, ...updatedTune });
      await dexie.pendingWrites.put({
        id: t.id,
        type: 'tune',
        operation: 'update',
        data: { id: t.id, ...updatedTune }
      });
    }
  }

  navigate(`/sets/${newSet.id}/edit`);
  return;
}


              if (value === 'action:addtags') {
                setShowBulkTagInput(true);
                return;
              }

              const [field, raw] = value.split(':');
              const fieldMap = {
                status: 'status',
                rating: 'rating',
              };
              const updateField = fieldMap[field];
              const newValue = raw || null;

              const confirmText = newValue
                ? `Set ${updateField} to "${newValue}"`
                : `Remove ${updateField}`;

              const confirmed = window.confirm(`${confirmText} for ${selectedTuneIds.length} tune(s)?`);
              if (!confirmed) return;

              for (const id of selectedTuneIds) {
                const ref = doc(db, 'users', user.uid, 'tunes', id);
                await updateDoc(ref, { [updateField]: newValue });
              }

              setSelectedTuneIds([]);
            }}
>
                    <option value="">
                      {selectedTuneIds.length > 0
                        ? `Bulk Actions on ${selectedTuneIds.length} tune${selectedTuneIds.length > 1 ? 's' : ''}`
                        : 'Bulk Actions…'}
                    </option>

                    <optgroup label="Actions">
                      <option value="action:delete">🗑️ Delete Selected</option>
                      <option value="action:addset">➕ Add to New Set</option>
                      <option value="action:addtags">🏷️ Add Tags to Selected</option>
                    </optgroup>
                    <optgroup label="Status">
                      <option value="status:Need to Learn">Mark as Need to Learn</option>
                      <option value="status:Learning">Mark as Learning</option>
                      <option value="status:Learned">Mark as Learned</option>
                      <option value="status:">Remove Status</option>
                    </optgroup>
                    <optgroup label="Rating">
                      <option value="rating:★">★</option>
                      <option value="rating:★★">★★</option>
                      <option value="rating:★★★">★★★</option>
                      <option value="rating:★★★★">★★★★</option>
                      <option value="rating:★★★★★">★★★★★</option>
                      <option value="rating:">Remove Rating</option>
                    </optgroup>
                  </select>
            </div>
          </div>

{showBulkTagInput && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected tunes:
    </p>

 <div
  style={{
    width: '100%',
    maxWidth: '400px',
    boxSizing: 'border-box',
    padding: '0 1rem',
  }}
>
  <CustomTagInput
    tags={bulkTags}
    setTags={setBulkTags}
    suggestions={prefs?.customTags || []}
    inputStyle={inputStyle}
  />
</div>


    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={handleBulkTagConfirm}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        ✅ Confirm Tags
      </button>

      <button
        onClick={() => {
          setShowBulkTagInput(false);
          setBulkTags([]);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}



</BorderedBox>

          {pendingDelete && (
            <div style={{ marginBottom: '20px', color: 'darkred' }}>
              Deleted {pendingDelete.tunes.length} tune(s)
              {pendingDelete.sets.length > 0 && ` from ${pendingDelete.sets.length} set(s)`}
              . <button onClick={undoDelete} style={{ ...inputStyle, color: 'blue' }}>Undo</button>
            </div>
          )}

          {filteredTunes.length === 0 ? (
            <p>No tunes found.</p>
          ) : (
<div
className="scroll-container"
  style={{
    overflowY: 'auto',
    paddingRight: '0px',
    paddingLeft: '0px',
    marginBottom: '2rem',
    boxSizing: 'border-box',
    height: `calc(100vh - ${collapsed ? 180 : 380}px)` // tweak as needed
  }}
>



              <ul className="card-list">
                {sortedTunes.map((tune) => (
<TuneCardBox
  key={tune.id}
  onClick={async () => {
    try {
      const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
      const snap = await getDoc(ref);
      const fullTune = snap.exists() ? snap.data() : null;

      if (activeSession && user && fullTune) {
        const sessions = fullTune.sessions || [];
        const alreadyIn = sessions.some(s => s.sessionId === activeSession.id);

    if (!alreadyIn) {
  setPendingSessionTune({ id: tune.id, title: tune.title });
  return;
}

      }

      const activeFilters = {
        searchQuery,
        filterKey,
        filterType,
        filterGenre,
        filterStatus,
        filterRating,
        filterRatingLabel,
        selectedTags
      };

      navigate(`/tune/${tune.id}`, {
        state: {
          searchTuneIds: sortedTunes.map(t => t.id),
          currentIndex: sortedTunes.findIndex(t => t.id === tune.id),
          activeFilters
        }
      });

    } catch (err) {
      console.error('Error tagging tune with session:', err);
      navigate(`/tune/${tune.id}`, {
        state: {
          searchTuneIds: sortedTunes.map(t => t.id),
          currentIndex: sortedTunes.findIndex(t => t.id === tune.id)
        }
      });
    }
  }}
>
  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '1 1 200px', minWidth: '160px' }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          toggleTuneSelection(tune.id);
        }}
        style={{
          padding: '7px',
          margin: '-7px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px',
          minHeight: '40px',
          touchAction: 'manipulation',
        }}
      >
        <StyledCheckbox
          checked={selectedTuneIds.includes(tune.id)}
          onChange={() => {}}
        />
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5ch' }}>
          <strong style={{ fontSize: '1rem' }}>{tune.title}</strong>
          <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
            - {[normalizeGenre(tune.genre), normalizeType(tune.type), normalizeKey(tune.key)].filter(Boolean).join(' / ')}
          </span>
        </div>

        {prefs?.showLyricsInSearch && tune.lyrics && (
          <div
            style={{
              marginTop: '4px',
              fontSize: '0.85em',
              color: '#888',
              whiteSpace: 'pre-line',
              lineHeight: '1.2em',
              maxHeight: '2.6em',
              overflow: 'hidden',
            }}
          >
            {tune.lyrics.split('\n').slice(0, 2).join('\n')}
          </div>
        )}
      </div>
    </div>

    {prefs?.showAbcInSearch && tune.abc && (
      <div style={{ flex: '0 0 auto', overflow: 'hidden', height: '50px' }}>
        <AbcPreview abc={tune.abc} width={320} scale={0.55} />
      </div>
    )}
  </div>

  {sessionConfirmations[tune.id] && (
    <div
      className="confirmation-message"
      style={{
        backgroundColor: '#003366',
        color: 'white',
        padding: '0.5rem 1rem',
        marginTop: '0.5rem',
        borderRadius: '6px',
        textAlign: 'center',
        cursor: 'pointer',
        fontSize: '0.9rem'
      }}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data();
            const filteredSessions = (data.sessions || []).filter(s => s.sessionId !== activeSession.id);
            await dexie.tunes.put({ id: tune.id, ...data, sessions: filteredSessions });

            if (!navigator.onLine) {
              await dexie.pendingWrites.put({
                id: tune.id,
                type: 'tune',
                operation: 'update',
                data: { id: tune.id, ...data, sessions: filteredSessions }
              });
            }

            setSessionConfirmations(prev => {
              const updated = { ...prev };
              delete updated[tune.id];
              return updated;
            });
          }
        } catch (err) {
          console.error('Error removing session from tune:', err);
          alert('Could not undo session assignment');
        }
      }}
    >
      Tune added to session. <span style={{ opacity: 0.8 }}>(Click to undo)</span>
    </div>
  )}
</TuneCardBox>



))}

              </ul>
            </div>
          )}
          </div>
        </>
      )}

  {pendingSessionTune && (
  <SessionPromptModal
    message={`Add "${pendingSessionTune.title}" to the current session?`}
    onConfirm={async () => {
      try {
        const ref = doc(db, 'users', user.uid, 'tunes', pendingSessionTune.id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const fullTune = snap.data();
        const sessions = fullTune.sessions || [];

        const fullSession = {
          sessionId: activeSession.id || '',
          location: activeSession.location || '',
          startedAt: activeSession.startedAt || new Date().toISOString()
        };

        const updatedSessions = [
          ...sessions.filter(s => s.sessionId !== fullSession.sessionId),
          fullSession
        ];

        await updateDoc(ref, { sessions: updatedSessions });
        await dexie.tunes.put({ id: pendingSessionTune.id, ...fullTune, sessions: updatedSessions });

        if (!navigator.onLine) {
          await dexie.pendingWrites.put({
            type: 'update',
            collection: 'tunes',
            docId: pendingSessionTune.id,
            data: { id: pendingSessionTune.id, ...fullTune, sessions: updatedSessions }
          });
        }

        setSessionConfirmations(prev => ({
          ...prev,
          [pendingSessionTune.id]: true
        }));

        setTimeout(() => {
          setSessionConfirmations(prev => {
            const updated = { ...prev };
            delete updated[pendingSessionTune.id];
            return updated;
          });
        }, 5000);
      } catch (err) {
        console.error('Failed to add tune to session', err);
      } finally {
        setPendingSessionTune(null);
      }
    }}
    onCancel={() => {
      navigate(`/tune/${pendingSessionTune.id}`);
      setPendingSessionTune(null);
    }}
  />
)}


    </PageWrapper>
  );
}
